import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';

import { paths } from '@/app/routes/paths';
import plusCircleIcon from '@/assets/icons/plus-circle.svg';
import { skipsOrganisationOnboarding } from '@/features/auth/lib/default-home-path';
import { onboardingPathForStep } from '@/features/onboarding/lib/onboarding-routing';
import { messages } from '@/shared/i18n';
import { cn } from '@/shared/lib/cn';
import { iconMaskStyle } from '@/shared/lib/icon-mask';
import { WORKFORCE_HUB_PRIMARY_PILL_CLASS } from '@/shared/lib/primary-action-button-classes';
import { currentOnboardingStep, isEmailVerified, isOnboardingComplete, useAuthStore } from '@/store';

interface RequireOnboardingCompleteProps {
  children: ReactNode;
}

const tw = messages.workspace;

/**
 * Blocks workspace access until organisation onboarding is complete, while
 * still allowing verification/dashboard gate states to render first.
 */
export function RequireOnboardingComplete({ children }: RequireOnboardingCompleteProps) {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();

  if (!user) {
    return null;
  }

  if (!isEmailVerified(user) || skipsOrganisationOnboarding(user) || isOnboardingComplete(user)) {
    return <>{children}</>;
  }

  const stepPath = onboardingPathForStep(currentOnboardingStep(user));
  const resumePath = stepPath === paths.dashboard ? paths.onboardingIdentity : stepPath;

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-[linear-gradient(180deg,rgba(255,255,255,0.4)_30.769%,rgba(150,222,174,0.4)_100%)]">
      <section className="flex min-h-0 w-full flex-1 flex-col items-center justify-start px-6 pb-12 pt-20 text-center sm:pt-24">
        <div className="flex w-full max-w-[760px] flex-col items-center gap-2 text-ink">
          <h1 className="text-[20px] font-semibold leading-normal text-ink">{tw.onboardingGate.title}</h1>
          <p className="max-w-[760px] text-[14px] font-normal leading-normal text-ink">{tw.onboardingGate.body}</p>
        </div>

        <button
          type="button"
          className={cn(WORKFORCE_HUB_PRIMARY_PILL_CLASS, 'mt-8')}
          onClick={() => {
            void navigate(resumePath);
          }}
        >
          <span>{tw.onboardingGate.cta}</span>
          <span aria-hidden className="block size-3 text-white" style={iconMaskStyle(plusCircleIcon)} />
        </button>
      </section>
    </div>
  );
}
